import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, ChapterHead } from "@/components/Section";

const CATS = ["bronze", "terre", "bronze", "bronze"];

const FILTER_KEYS = ["all", "bronze", "terre"];
const FILTER_TESTIDS = ["sculpture-filter-all-btn", "sculpture-filter-bronze-btn", "sculpture-filter-terracotta-btn"];

const Gallery = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const items = t.gallery.items.map((item, i) => ({ ...item, cat: CATS[i], index: i }));
  const visible = items.filter((w) => filter === "all" || w.cat === filter);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="sculptures" data-testid="sculpture-gallery-section" className="relative px-5 md:px-10 py-28 md:py-40 scroll-mt-20">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <ChapterHead kicker={t.gallery.kicker} title={t.gallery.title} testId="gallery" />
          <Reveal delay={0.15} className="max-w-md mb-14 md:mb-20">
            <p className="text-ink2 text-sm md:text-base leading-relaxed">{t.gallery.intro}</p>
          </Reveal>
        </div>

        <div className="flex flex-wrap gap-3 mb-12" data-testid="sculpture-filters">
          {FILTER_KEYS.map((k, i) => (
            <button
              key={k}
              data-testid={FILTER_TESTIDS[i]}
              onClick={() => setFilter(k)}
              className={`px-5 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                filter === k ? "bg-ink text-paper border-ink" : "border-line text-ink2 hover:border-terra hover:text-terra"
              }`}
            >
              {t.gallery.filters[i]}
            </button>
          ))}
        </div>

        <motion.div layout data-testid="sculpture-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <AnimatePresence mode="popLayout">
            {visible.map((w, i) => (
              <motion.button
                layout
                key={w.title}
                data-testid={`sculpture-card-${w.index}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(w)}
                onMouseMove={onMove}
                className={`spotlight-card group relative text-left bg-inkdeep overflow-hidden border border-linedark/50 hover:border-gold/40 transition-colors ${i % 2 === 1 ? "sm:mt-10" : ""}`}
              >
                <div className="relative aspect-[3/4] p-6 flex flex-col justify-between">
                  <span className="font-display italic font-light text-7xl text-gold/20 group-hover:text-gold/45 transition-colors duration-700 leading-none">
                    {String(w.index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-gold mb-2">{w.medium} · {w.year}</p>
                    <h3 className="font-display text-2xl text-paper leading-tight group-hover:text-gold transition-colors duration-500">{w.title}</h3>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="sculpture-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[90] bg-inkdeep/95 backdrop-blur-md flex items-center justify-center p-5 md:p-12"
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setSelected(null)}
              className="absolute top-6 right-6 text-paper/70 hover:text-gold transition-colors p-2"
              aria-label={t.gallery.close}
            >
              <X size={26} />
            </button>
            <motion.div
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-xl w-full bg-inkdeep border border-linedark p-10 md:p-14 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-gold mb-4">{selected.medium} · {selected.year}</p>
              <h3 data-testid="lightbox-title" className="font-display font-light text-4xl md:text-5xl text-paper leading-tight mb-5">{selected.title}</h3>
              <p className="text-paper/60 leading-relaxed">{selected.desc}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
